import classNames from 'classnames';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Icon } from 'tdesign-icons-react';
import { Button, InputNumber, Select, SelectValue } from 'tdesign-react';

import { EditorDel } from '@/components/icon';
import { Operand, OperandType, OperatorsType } from '@/interface/numberSysytem';

import { NUM_CHANGE } from '../..';
import NumberSystemSelect from '../../numberSysytemsSelect';
import { CardData } from '..';
import styles from './index.module.less';

// 卡片数据类型定义
export interface ItemCardData {
  id: string;
  op: OperatorsType;
  operand: Operand
}

interface IProps {
  fileName: string;
  disabled?: boolean;
  onChange: (val: ItemCardData[]) => void;
  value: CardData
}
const ItemCard: React.FC<IProps> = (props) => {
  const { fileName, disabled, onChange, value } = props;

  // 卡片数据状态
  const cards = useMemo(() => value?.opGroup, [value]);

  // 新增卡片
  const handleAddCard = () => {
    const newCard: ItemCardData = {
      id: dayjs().valueOf() + cards?.length.toString(),
      op: OperatorsType.ADD,
      operand: {
        operandType: OperandType.CONSTANT,
        variableNumId: '',
        constantVal: 0,
      },
    };
    const result = [...cards, newCard];
    onChange(result);
  };

  // 删除卡片
  const handleDeleteCard = (id: string) => {
    const result = cards.filter(card => card.id !== id);
    onChange(result);
  };

  const handleChangeCalculate = (id: string, val: SelectValue) => {
    const result = cards.map(card => (card.id === id ? { ...card, op: val as OperatorsType } : card));
    onChange(result);
  };

  const handleChangeOperandType = (id: string, val: SelectValue) => {
    const result = cards.map(card => (card.id === id ? {
      ...card,
      operand: {
        ...card.operand,
        operandType: val as OperandType,
      },
    } : card));
    onChange(result);
  };

  const handleChangeVariableNumId = (id: string, val: SelectValue) => {
    const result = cards.map(card => (card.id === id ? {
      ...card,
      operand: {
        ...card.operand,
        variableNumId: val as string,
      },
    } : card));
    onChange(result);
  };

  const handleChangeConstantVal = (id: string, val: SelectValue) => {
    const jv = val as number;
    let v = jv > 10000 ? 10000 : jv;
    v = v < -10000 ? -10000 : v;
    const result = cards.map(card => (card.id === id ? {
      ...card,
      operand: {
        ...card.operand,
        constantVal: v,
      },
    } : card));
    onChange(result);
  };

  const { opOptions = [] } = useMemo(() => {
    if (fileName === NUM_CHANGE) {
      return {
        opOptions: [
          { label: '加', value: OperatorsType.ADD },
          { label: '减', value: OperatorsType.SUB },
          { label: '乘', value: OperatorsType.MULTI },
          { label: '除', value: OperatorsType.DIVIDE },
          { label: '等于', value: OperatorsType.EQ },
        ],
      };
    }
    return {
      opOptions: [
        { label: '加', value: OperatorsType.ADD },
        { label: '减', value: OperatorsType.SUB },
        { label: '乘', value: OperatorsType.MULTI },
        { label: '除', value: OperatorsType.DIVIDE },
        { label: '等于', value: OperatorsType.EQ },
        { label: '大于', value: OperatorsType.GT },
        { label: '小于', value: OperatorsType.LT },
        { label: '小于等于', value: OperatorsType.LTE },
        { label: '大于等于', value: OperatorsType.GTE },
      ],
    };
  }, [fileName]);

  return (
    <div className={styles.content}>
      {
        cards.map((card, index) => (
          <div className={styles.selectContentCard} key={index + card?.id}>
            {
              index === 0 ? null
                : <div className={styles.calculate}>
                  <Select
                    value={card?.op}
                    style={{ width: '98px' }}
                    onChange={(val) => {
                      handleChangeCalculate(card.id, val);
                    }}
                    disabled={disabled}
                    placeholder='计算规则'
                    options={opOptions}
                  />
                </div>
            }
            <div className={styles.selectContent}>
              <div className={styles.selectContentLeft}>
                <Select
                  value={card?.operand?.operandType}
                  style={{ width: '98px' }}
                  placeholder='条件选择'
                  disabled={disabled}
                  onChange={(val) => {
                    handleChangeOperandType(card.id, val);
                  }}
                  options={
                    index === 0 ? [
                      { label: '变量', value: OperandType.VARIABLE },
                    ] : [
                      { label: '变量', value: OperandType.VARIABLE },
                      { label: '固定数值', value: OperandType.CONSTANT },
                    ]}
                />
              </div>
              <div className={styles.selectContentRight}>
                {
                  card?.operand?.operandType === OperandType.VARIABLE
                    ? <NumberSystemSelect
                      value={card?.operand?.variableNumId}
                      disabled={disabled}
                      showError={true}
                      onChange={(val) => {
                        handleChangeVariableNumId(card.id, val);
                      }} />
                    : <InputNumber
                      defaultValue={undefined}
                      disabled={disabled}
                      min={-10000}
                      max={10000}
                      onChange={(val) => {
                        handleChangeConstantVal(card.id, val);
                      }}
                      theme="normal"
                      placeholder='请输入数值'
                      style={{ flex: 1, margin: '0 6px' }}
                      value={card?.operand?.constantVal}
                    />
                }
                <Button
                  className={classNames(styles.delete, {
                    'nova-disabled-opacity': disabled || index === 0,
                  })}
                  disabled={disabled || index === 0}
                  theme="default"
                  onClick={() => {
                    handleDeleteCard(card.id);
                  }}
                >
                  <EditorDel />
                </Button>
              </div>
            </div>
          </div>
        ))
      }
      <div className={styles.addCard}>
        <Button
          theme="default"
          size='medium'
          style={{ width: '100%', height: 32 }}
          onClick={handleAddCard}
          icon={<Icon name="add" />}
          disabled={disabled}
        >
          新增变量或数值
        </Button>
      </div>
    </div>
  );
};

export default ItemCard;
